import React, { useState } from 'react';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import useMobile from '../hooks/useMobile';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: 'Hi! I\'m your Krafty assistant. How can I help you today?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const isMobile = useMobile();

  const handleSend = async () => {
    if (inputValue.trim()) {
      // Add user message
      const userMessage = { type: 'user', text: inputValue };
      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
      
      // Add loading message
      const loadingMessage = { type: 'bot', text: '...', loading: true };
      setMessages(prev => [...prev, loadingMessage]);
      
      try {
        // Call your LLM server - using the Gemma format from your server
        const response = await fetch(`${process.env.REACT_APP_LLM_SERVER_URL || 'http://localhost:6969'}/v1/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(process.env.REACT_APP_LLM_API_TOKEN && {
              'Authorization': `Bearer ${process.env.REACT_APP_LLM_API_TOKEN}`
            })
          },
          body: JSON.stringify({
            model: 'gemma-3-4b',
            messages: [
              {
                role: 'system',
                content: 'You are a helpful assistant for Krafty Restaurants, a company that builds custom mobile apps for restaurants. Help users with questions about our services, pricing (payment processing + 5% fee), features, and getting started. Be friendly and professional.'
              },
              { role: 'user', content: inputValue }
            ],
            temperature: 0.7,
            max_tokens: 500,
            stream: false
          }),
        });
        
        if (!response.ok) {
          throw new Error('Server response was not ok');
        }
        
        const data = await response.json();
        
        // Remove loading message and add actual response
        setMessages(prev => {
          const filtered = prev.filter(msg => !msg.loading);
          return [...filtered, { 
            type: 'bot', 
            text: data.choices?.[0]?.message?.content || 'I apologize, I couldn\'t generate a response.' 
          }];
        });
        
      } catch (error) {
        console.error('Error calling LLM server:', error);
        
        // Remove loading message and show error
        setMessages(prev => {
          const filtered = prev.filter(msg => !msg.loading);
          return [...filtered, { 
            type: 'bot', 
            text: 'I apologize, but I\'m having trouble connecting right now. Please try again or contact support at 1-800-KRAFTY-1.' 
          }];
        });
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        data-chatbot-trigger
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#7c3aed',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          display: isOpen ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          zIndex: 1000,
          transition: 'transform 0.3s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <MessageCircle size={28} />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: isMobile ? 'calc(100% - 40px)' : '400px',
          height: isMobile ? 'calc(100% - 40px)' : '600px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            backgroundColor: '#7c3aed',
            color: 'white',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Bot size={24} />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Krafty Assistant</h3>
                <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.9 }}>Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {messages.map((message, idx) => (
              <div
                key={idx}
                style={{
                  alignSelf: message.type === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%'
                }}
              >
                <div style={{
                  backgroundColor: message.type === 'user' ? '#7c3aed' : '#f3f4f6',
                  color: message.type === 'user' ? 'white' : '#1a1a1a',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  ...(message.type === 'user' 
                    ? { borderBottomRightRadius: '4px' }
                    : { borderBottomLeftRadius: '4px' })
                }}>
                  {message.loading ? (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <span style={{ animation: 'pulse 1.4s infinite' }}>•</span>
                      <span style={{ animation: 'pulse 1.4s infinite 0.2s' }}>•</span>
                      <span style={{ animation: 'pulse 1.4s infinite 0.4s' }}>•</span>
                    </div>
                  ) : (
                    message.text
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div style={{
            padding: '20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '10px'
          }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none'
              }}
            />
            <button
              onClick={handleSend}
              style={{
                backgroundColor: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;